import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Chip, CircularProgress,
  LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
  Switch, FormControlLabel, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import TokenIcon from '@mui/icons-material/Token';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import MockupAdminTab from './MockupAdminTab';
import { db } from '../../../firebase/config';
import { doc, updateDoc, serverTimestamp, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  useIsApplabAdmin, useApplabConfig, useApplabQuotas, useApplabUsage,
} from '../hooks/useAdminData';
import { useAuth } from '../../../contexts/AuthContext';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../constants';

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtTokens(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function fmtCost(n) { return `$${(n || 0).toFixed(4)}`; }

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

// ── Shared cell style ─────────────────────────────────────────────────────────
const cellSx = { color: '#ccc', borderColor: 'rgba(255,255,255,0.06)', fontSize: '0.8rem', py: 1 };
const headSx = { color: '#888', borderColor: 'rgba(255,255,255,0.08)', fontSize: '0.7rem', fontWeight: 700, py: 1 };

// ── Overview tab ──────────────────────────────────────────────────────────────
function OverviewTab({ config, usage, quotas }) {
  const month = currentMonth();
  const totalUsed   = config?.month === month ? (config?.tokens_used_this_month || 0) : 0;
  const totalLimit  = config?.total_monthly_limit || 10_000_000;
  const pct         = Math.min(100, Math.round((totalUsed / totalLimit) * 100));
  const totalCost   = usage.filter(u => {
    if (!u.timestamp) return false;
    const d = u.timestamp.toDate ? u.timestamp.toDate() : new Date(u.timestamp);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` === month;
  }).reduce((s, u) => s + (u.cost_usd || 0), 0);
  const activeUsers = quotas.filter(q => (q.tokens_used_this_month || 0) > 0).length;

  async function toggleBlock(key) {
    await updateDoc(doc(db, 'applab_config', 'global'), {
      [key]: config?.[key] === false ? true : false,
      updated_at: serverTimestamp(),
    });
  }

  const BLOCK_SWITCHES = [
    { key: 'ai_chat_enabled',            label: 'ai.chat',            ai: true  },
    { key: 'ai_extract_enabled',         label: 'ai.extract',         ai: true  },
    { key: 'qr_generate_enabled',        label: 'qr.generate',        ai: false },
    { key: 'chart_render_enabled',       label: 'chart.render',       ai: false },
    { key: 'wordcloud_generate_enabled', label: 'wordcloud.generate', ai: false },
  ];

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
        {[
          { label: 'Tokens denna månad', value: fmtTokens(totalUsed), sub: `av ${fmtTokens(totalLimit)}` },
          { label: 'Kostnad denna månad', value: `$${totalCost.toFixed(2)}`, sub: 'USD' },
          { label: 'Aktiva användare', value: activeUsers, sub: 'denna månad' },
          { label: 'Anrop (senaste 100)', value: usage.length, sub: `${usage.filter(u=>u.success).length} lyckade` },
        ].map(({ label, value, sub }) => (
          <Paper key={label} sx={{ p: 2.5, bgcolor: '#1a1a1a', borderRadius: 2 }}>
            <Typography fontSize="0.7rem" sx={{ color: '#666', mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography fontSize="1.6rem" fontWeight={800} sx={{ color: 'white', lineHeight: 1 }}>{value}</Typography>
            <Typography fontSize="0.75rem" sx={{ color: '#555', mt: 0.5 }}>{sub}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: 'white' }}>Total månadskvot</Typography>
          <Typography fontSize="0.8rem" sx={{ color: '#888' }}>{pct}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8, borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: pct > 80 ? '#f44336' : APPLAB_ACCENT },
          }}
        />
        <Typography fontSize="0.75rem" sx={{ color: '#555', mt: 1 }}>
          {fmtTokens(totalUsed)} / {fmtTokens(totalLimit)} tokens
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
        <Typography fontWeight={700} fontSize="0.85rem" sx={{ mb: 2, color: 'white' }}>Kill-switch per block</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {BLOCK_SWITCHES.map(({ key, label, ai }) => {
            const enabled = config?.[key] !== false;
            return (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={enabled}
                    onChange={() => toggleBlock(key)}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: enabled ? APPLAB_ACCENT : '#555' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontSize="0.85rem" sx={{ color: '#ccc', fontFamily: 'monospace' }}>{label}</Typography>
                    <Chip label={ai ? 'AI' : 'Gratis'} size="small"
                      sx={{ height: 16, fontSize: '0.6rem', bgcolor: ai ? 'rgba(255,107,53,0.15)' : 'rgba(0,137,123,0.15)', color: ai ? '#FF6B35' : APPLAB_ACCENT }} />
                    <Typography fontSize="0.75rem" sx={{ color: enabled ? '#4CAF50' : '#f44336' }}>
                      {enabled ? 'aktivt' : 'pausat'}
                    </Typography>
                  </Box>
                }
              />
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab({ quotas, usage }) {
  const [editUser, setEditUser] = useState(null);
  const [newLimit, setNewLimit] = useState('');
  const [saving, setSaving] = useState(false);
  const month = currentMonth();

  const usageCostByUid = usage.reduce((acc, u) => {
    if (!u.uid) return acc;
    const d = u.timestamp?.toDate ? u.timestamp.toDate() : null;
    const m = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` : '';
    if (m !== month) return acc;
    acc[u.uid] = (acc[u.uid] || 0) + (u.cost_usd || 0);
    return acc;
  }, {});

  const lastUsedByUid = usage.reduce((acc, u) => {
    if (!u.uid || acc[u.uid]) return acc;
    acc[u.uid] = u.timestamp;
    return acc;
  }, {});

  async function saveLimit() {
    const lim = parseInt(newLimit, 10);
    if (!lim || lim < 0) return;
    setSaving(true);
    await updateDoc(doc(db, 'applab_quotas', editUser.id), {
      monthly_limit: lim,
      updated_at: serverTimestamp(),
    });
    setSaving(false);
    setEditUser(null);
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Namn', 'E-post', 'Kvot', 'Använt', '%', 'Kostnad', 'Senast', ''].map(h => (
                <TableCell key={h} sx={headSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {quotas.map(q => {
              const qMonth = q.month || '';
              const used   = qMonth !== month ? 0 : (q.tokens_used_this_month || 0);
              const limit  = q.monthly_limit || 1_000_000;
              const pct    = Math.min(100, Math.round((used / limit) * 100));
              const cost   = usageCostByUid[q.id] || 0;
              return (
                <TableRow key={q.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                  <TableCell sx={cellSx}>{q.displayName || '—'}</TableCell>
                  <TableCell sx={cellSx}>{q.email || '—'}</TableCell>
                  <TableCell sx={cellSx}>{fmtTokens(limit)}</TableCell>
                  <TableCell sx={cellSx}>{fmtTokens(used)}</TableCell>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate" value={pct}
                        sx={{ width: 48, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: pct > 80 ? '#f44336' : APPLAB_ACCENT } }}
                      />
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{pct}%</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>{fmtCost(cost)}</TableCell>
                  <TableCell sx={cellSx}>{fmtTs(lastUsedByUid[q.id])}</TableCell>
                  <TableCell sx={cellSx}>
                    <EditIcon
                      sx={{ fontSize: 16, color: '#555', cursor: 'pointer', '&:hover': { color: APPLAB_ACCENT } }}
                      onClick={() => { setEditUser(q); setNewLimit(String(q.monthly_limit || 1_000_000)); }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!editUser} onClose={() => setEditUser(null)} PaperProps={{ sx: { bgcolor: '#1e1e1e', color: 'white', minWidth: 340 } }}>
        <DialogTitle sx={{ color: 'white' }}>Justera kvot — {editUser?.displayName}</DialogTitle>
        <DialogContent>
          <TextField
            label="Månadskvot (tokens)"
            value={newLimit}
            onChange={e => setNewLimit(e.target.value)}
            type="number"
            fullWidth
            size="small"
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { color: 'white' }, '& label': { color: '#888' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} sx={{ color: '#888' }}>Avbryt</Button>
          <Button onClick={saveLimit} disabled={saving} variant="contained" sx={{ bgcolor: APPLAB_ACCENT }}>
            {saving ? 'Sparar...' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Log tab ───────────────────────────────────────────────────────────────────
function LogTab({ usage }) {
  const [filterUid, setFilterUid] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);

  const rows = usage.filter(u => {
    if (onlyErrors && u.success !== false) return false;
    if (filterUid && !(u.uid || '').includes(filterUid) && !(u.displayName || '').includes(filterUid)) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Filtrera på UID/namn..."
          value={filterUid}
          onChange={e => setFilterUid(e.target.value)}
          size="small"
          sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.05)' }, '& input::placeholder': { color: '#555' } }}
        />
        <FormControlLabel
          control={<Switch checked={onlyErrors} onChange={e => setOnlyErrors(e.target.checked)} size="small" />}
          label={<Typography fontSize="0.8rem" sx={{ color: '#ccc' }}>Endast fel</Typography>}
        />
        <Typography fontSize="0.8rem" sx={{ color: '#555', ml: 'auto' }}>{rows.length} poster</Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Tid', 'UID', 'Block', 'Modell', 'Tokens', 'Kostnad', 'Status', 'ms'].map(h => (
                <TableCell key={h} sx={headSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(u => (
              <TableRow key={u.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                <TableCell sx={cellSx}>{fmtTs(u.timestamp)}</TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.uid?.slice(0, 8)}…</TableCell>
                <TableCell sx={cellSx}>{u.block}</TableCell>
                <TableCell sx={cellSx}>{u.model || '—'}</TableCell>
                <TableCell sx={cellSx}>{fmtTokens(u.tokens_total)}</TableCell>
                <TableCell sx={cellSx}>{fmtCost(u.cost_usd)}</TableCell>
                <TableCell sx={cellSx}>
                  <Chip
                    label={u.success ? 'OK' : 'FEL'}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: u.success ? '#4CAF5022' : '#f4433622',
                      color:   u.success ? '#4CAF50'   : '#f44336',
                    }}
                  />
                </TableCell>
                <TableCell sx={cellSx}>{u.duration_ms || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────
function SettingsTab({ config }) {
  const [vals, setVals] = useState({
    total_monthly_limit:  config?.total_monthly_limit  || 10_000_000,
    per_request_limit:    config?.per_request_limit    || 20_000,
    default_user_limit:   config?.default_user_limit   || 1_000_000,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true);
    await updateDoc(doc(db, 'applab_config', 'global'), {
      ...vals,
      total_monthly_limit: Number(vals.total_monthly_limit),
      per_request_limit:   Number(vals.per_request_limit),
      default_user_limit:  Number(vals.default_user_limit),
      updated_at: serverTimestamp(),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const field = (label, key, helper) => (
    <Box sx={{ mb: 3 }}>
      <TextField
        label={label}
        value={vals[key]}
        onChange={e => setVals(v => ({ ...v, [key]: e.target.value }))}
        type="number"
        size="small"
        helperText={helper}
        sx={{
          '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.05)' },
          '& label': { color: '#888' },
          '& .MuiFormHelperText-root': { color: '#555' },
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 400 }}>
      {field('Total månadskvot (tokens)', 'total_monthly_limit', 'Gäller hela tjänsten, default 10M')}
      {field('Max tokens per anrop', 'per_request_limit', 'Gateway avvisar anrop över denna gräns, default 20K')}
      {field('Standard användarkvot (tokens)', 'default_user_limit', 'Gäller nya användare, default 1M')}
      {saved && <Alert severity="success" sx={{ mb: 2, bgcolor: '#1a2e1a', color: '#81c784' }}>Sparat!</Alert>}
      <Button
        variant="contained"
        onClick={save}
        disabled={saving}
        sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' } }}
      >
        {saving ? 'Sparar...' : 'Spara inställningar'}
      </Button>
    </Box>
  );
}

// ── Sync tab ──────────────────────────────────────────────────────────────────
function SyncTab() {
  const { currentUser } = useAuth();
  const [logs, setLogs]         = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [runError, setRunError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'applab_sync_logs'), orderBy('runAt', 'desc'), limit(10));
    const unsub = onSnapshot(q, snap => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLogsLoading(false);
    }, () => setLogsLoading(false));
    return unsub;
  }, []);

  async function runSync() {
    setRunning(true);
    setResult(null);
    setRunError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/sync-applab-uppdrag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Synkronisering misslyckades');
      setResult(data);
    } catch (e) {
      setRunError(e.message);
    } finally {
      setRunning(false);
    }
  }

  const lastLog = logs[0];

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2, mb: 3 }}>
        <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: 'white', mb: 0.5 }}>
          Synkronisera uppdragsnamn
        </Typography>
        <Typography fontSize="0.78rem" sx={{ color: '#666', mb: 2 }}>
          Uppdaterar projectNumber och name på alla kopplade uppdrag. Tar bort kopplingar till projekt som inte längre finns.
        </Typography>
        {lastLog && (
          <Typography fontSize="0.75rem" sx={{ color: '#555', mb: 2 }}>
            Senaste körning: {fmtTs(lastLog.runAt)} · {lastLog.appsChecked} appar · {lastLog.updated?.length ?? 0} uppdaterade · {lastLog.removed?.length ?? 0} borttagna
          </Typography>
        )}
        {result && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: '#0d2b0d', color: '#81c784', fontSize: '0.8rem' }}>
            Klart! {result.appsChecked} appar kontrollerade · {result.updated} uppdaterade · {result.removed} borttagna · {result.duration_ms} ms
          </Alert>
        )}
        {runError && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#2e0000', color: '#ef9a9a', fontSize: '0.8rem' }}>
            {runError}
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={runSync}
          disabled={running}
          startIcon={running ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SyncIcon sx={{ fontSize: 16 }} />}
          sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, textTransform: 'none' }}
        >
          {running ? 'Kör synkronisering…' : 'Kör synkronisering nu'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
        <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: 'white', mb: 2 }}>
          Senaste 10 körningar
        </Typography>
        {logsLoading ? (
          <CircularProgress size={20} sx={{ color: APPLAB_ACCENT }} />
        ) : logs.length === 0 ? (
          <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Inga körningar ännu.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Tid', 'Av', 'Appar', 'Uppdaterade', 'Borttagna', 'ms'].map(h => (
                  <TableCell key={h} sx={headSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                  <TableCell sx={cellSx}>{fmtTs(log.runAt)}</TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.triggeredBy === 'scheduler' ? 'Schema' : (log.triggeredBy || '').slice(0, 8) + '…'}
                  </TableCell>
                  <TableCell sx={cellSx}>{log.appsChecked ?? '—'}</TableCell>
                  <TableCell sx={cellSx}>
                    <Chip
                      label={log.updated?.length ?? 0}
                      size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: (log.updated?.length ?? 0) > 0 ? APPLAB_ACCENT + '22' : 'rgba(255,255,255,0.05)', color: (log.updated?.length ?? 0) > 0 ? APPLAB_ACCENT : '#555' }}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Chip
                      label={log.removed?.length ?? 0}
                      size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: (log.removed?.length ?? 0) > 0 ? '#f4433622' : 'rgba(255,255,255,0.05)', color: (log.removed?.length ?? 0) > 0 ? '#f44336' : '#555' }}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>{log.duration_ms ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────
export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useIsApplabAdmin();
  const config = useApplabConfig();
  const { quotas, loading: quotasLoading } = useApplabQuotas();
  const { usage, loading: usageLoading } = useApplabUsage();
  const [tab, setTab] = useState(0);

  if (adminLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress sx={{ color: APPLAB_ACCENT }} />
    </Box>
  );

  if (!isAdmin) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <Alert severity="error">Du har inte behörighet att se admin-panelen.</Alert>
    </Box>
  );

  const loading = quotasLoading || usageLoading;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', pb: 6 }}>
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box onClick={() => navigate('/applab')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> AppLAB
          </Box>
          <Typography sx={{ color: '#444' }}>›</Typography>
          <Typography fontWeight={700} fontSize="0.9rem">
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span> Admin
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 3,
            '& .MuiTab-root': { color: '#666', fontSize: '0.8rem', textTransform: 'none' },
            '& .Mui-selected': { color: APPLAB_ACCENT },
            '& .MuiTabs-indicator': { bgcolor: APPLAB_ACCENT },
          }}
        >
          <Tab icon={<TokenIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Översikt" />
          <Tab icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Användare (${quotas.length})`} />
          <Tab icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Logg" />
          <Tab icon={<TuneIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Inställningar" />
          <Tab icon={<SyncIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Synkronisering" />
          <Tab icon={<WebAssetIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="MockupN" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress sx={{ color: APPLAB_ACCENT }} />
          </Box>
        ) : (
          <>
            {tab === 0 && <OverviewTab config={config} usage={usage} quotas={quotas} />}
            {tab === 1 && <UsersTab quotas={quotas} usage={usage} />}
            {tab === 2 && <LogTab usage={usage} />}
            {tab === 3 && <SettingsTab config={config} />}
            {tab === 4 && <SyncTab />}
            {tab === 5 && <MockupAdminTab />}
          </>
        )}
      </Container>
    </Box>
  );
}
